import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';

// ── Formátovací pomůcky ───────────────────────────────────────────────────────
const fmt = (n) => String(n).padStart(2, '0');
const num = (v) => Math.round(v || 0).toLocaleString('cs-CZ');
const kc = (v) => `${num(v)} Kč`;

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${fmt(m)}m` : `${m}m`;
};

const shortDate = (dateKey) => {
  const d = new Date(`${dateKey}T12:00:00`);
  if (isNaN(d.getTime())) return dateKey;
  return `${d.getDate()}.${d.getMonth() + 1}.`;
};

// ── Karta ─────────────────────────────────────────────────────────────────────
function Card({ title, right, children, isDarkMode, bodyClassName = '' }) {
  return (
    <div className={`rounded-lg shadow border p-3 h-full flex flex-col ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2 shrink-0">
        <h3 className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{title}</h3>
        {right}
      </div>
      <div className={`flex-1 min-h-0 flex flex-col ${bodyClassName}`}>{children}</div>
    </div>
  );
}

// ── Mini sparkline (spojnicový graf, roztahuje se na celou dostupnou výšku) ──
// - vlevo osa s hodnotami (0 / 25 % / 50 % / 75 % / max) jako HTML overlay – SVG
//   používá preserveAspectRatio="none" (neuniformní škálování), takže <text> uvnitř
//   by se při roztažení deformoval; popisky proto stojí mimo SVG.
// - začátky týdnů (pondělí) zvýrazněné čárkovanou svislicí
// - najetí myší na libovolný bod ukáže přesnou hodnotu daného dne
function Sparkline({ points, dates = [], color = '#3b82f6', formatValue = (v) => v }) {
  const W = 300;
  const H = 100; // logická souřadnicová soustava; skutečná výška se řídí CSS (flex-1)
  const padL = 4;
  const padR = 2;
  const padY = 8;
  const maxV = Math.max(...points, 1);
  const innerW = W - padL - padR;
  const innerH = H - padY * 2;
  const x = (i) => padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v) => padY + innerH - (v / maxV) * innerH;
  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${(padY + innerH).toFixed(1)} L${x(0).toFixed(1)},${(padY + innerH).toFixed(1)} Z`;
  const last = points[points.length - 1] || 0;
  const half = Math.floor(points.length / 2) || 1;
  const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
  const diff = avg(points.slice(half)) - avg(points.slice(0, half));
  const trend = diff > (maxV * 0.03) ? '↗' : diff < -(maxV * 0.03) ? '↘' : '→';

  // Začátky týdnů (pondělí) – vždy označit; bez dat fallback na pár rovnoměrných bodů
  const mondayIdx = dates.length
    ? points.map((_, i) => i).filter((i) => {
        const d = new Date(`${dates[i]}T12:00:00`);
        return !isNaN(d.getTime()) && d.getDay() === 1;
      })
    : [];
  const fallbackCount = Math.min(5, points.length);
  const fallbackStep = Math.max(1, Math.round((points.length - 1) / (fallbackCount - 1 || 1)));
  const labelIdx = mondayIdx.length
    ? mondayIdx
    : [...new Set(Array.from({ length: fallbackCount }, (_, i) => Math.min(points.length - 1, i * fallbackStep)))];

  // Osa hodnot vlevo (0 / 25 % / 50 % / 75 % / max)
  const axisTicks = [...new Set([0, Math.round(maxV * 0.25), Math.round(maxV * 0.5), Math.round(maxV * 0.75), maxV])];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 flex">
        {/* osa hodnot vlevo – HTML text, ne SVG (aby se nedeformoval neuniformním škálováním) */}
        <div className="relative w-9 shrink-0 h-full text-[8px] text-gray-400 dark:text-gray-500">
          {axisTicks.map((v, idx) => {
            const isMax = idx === axisTicks.length - 1;
            const isMin = idx === 0;
            if (isMax) {
              return <span key={idx} className="absolute top-0 right-1 whitespace-nowrap">{formatValue(v)}</span>;
            }
            if (isMin) {
              return <span key={idx} className="absolute bottom-0 right-1 whitespace-nowrap">{formatValue(v)}</span>;
            }
            return (
              <span
                key={idx}
                className="absolute right-1 -translate-y-1/2 whitespace-nowrap"
                style={{ top: `${(y(v) / H) * 100}%` }}
              >
                {formatValue(v)}
              </span>
            );
          })}
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full flex-1 min-h-0" preserveAspectRatio="none">
          {/* vodorovné pomocné čáry (rovné čáry se škálováním nedeformují, jen text) */}
          {axisTicks.map((v, idx) => {
            const yy = y(v);
            return <line key={idx} x1={padL} x2={W - padR} y1={yy} y2={yy} className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />;
          })}
          {/* svislé značky dnů, začátky týdnů zvýrazněné čárkovaně */}
          {points.map((_, i) => {
            const isWeekStart = mondayIdx.includes(i);
            return (
              <line
                key={i}
                x1={x(i)} x2={x(i)}
                y1={padY} y2={padY + innerH}
                className={isWeekStart ? 'stroke-gray-400 dark:stroke-gray-500' : 'stroke-gray-200 dark:stroke-gray-700'}
                strokeWidth={isWeekStart ? 1.2 : 1}
                strokeDasharray={isWeekStart ? '2,2' : undefined}
                opacity={isWeekStart ? 0.9 : 0.2}
              />
            );
          })}
          <path d={area} fill={color} opacity="0.12" />
          <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {/* neviditelné body přes celou čáru – hodnota konkrétního dne při najetí myší */}
          {points.map((v, i) => (
            <circle key={i} cx={x(i)} cy={y(v)} r="6" fill="transparent">
              <title>{`${dates[i] ? shortDate(dates[i]) : `#${i + 1}`}: ${formatValue(v)}`}</title>
            </circle>
          ))}
          {labelIdx.map((i) => (
            <circle key={`d-${i}`} cx={x(i)} cy={y(points[i])} r="2" fill={color} />
          ))}
        </svg>
      </div>
      {dates.length > 0 && (
        <div className="relative h-3 text-[8px] text-gray-400 dark:text-gray-500 shrink-0">
          {labelIdx.map((i) => (
            <span
              key={i}
              className="absolute -translate-x-1/2 first:translate-x-0 last:-translate-x-full"
              style={{ left: `${(x(i) / W) * 100}%` }}
            >
              {shortDate(dates[i])}
            </span>
          ))}
        </div>
      )}
      <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 shrink-0">
        <span>Ø {formatValue(Math.round(avg(points)))}</span>
        <span>dnes {formatValue(last)} {trend}</span>
      </div>
    </div>
  );
}

// ── Mini sloupcový graf (hodiny dne / šarže; vyplní výšku svého rodiče) ──────
function MiniBarChart({ items, color = '#3b82f6', highlightIndex = -1 }) {
  const maxV = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="h-full flex items-end gap-[2px]">
      {items.map((it, i) => {
        const pct = it.value > 0 ? Math.max(6, Math.round((it.value / maxV) * 100)) : 0;
        return (
          <div key={i} className="flex-1 h-full flex items-end" title={`${it.label}: ${it.value}`}>
            <div
              className="w-full rounded-t transition-all"
              style={{
                height: it.value > 0 ? `${pct}%` : '2px',
                minHeight: it.value > 0 ? '2px' : undefined,
                backgroundColor: i === highlightIndex ? '#f59e0b' : color,
                opacity: it.value > 0 ? 1 : 0.25
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Grupovaný sloupcový graf (2 metriky na měsíc, roztahuje se na celou výšku) ─
// vlevo osa s hodnotami (0 / 25 % / 50 % / 75 % / max)
function DualBarChart({ months, aKey, bKey, aColor, bColor, formatValue }) {
  const maxV = Math.max(...months.map((m) => Math.max(m[aKey], m[bKey])), 1);
  const axisFractions = [1, 0.75, 0.5, 0.25, 0];
  return (
    <div className="flex-1 min-h-0 flex gap-1">
      <div className="relative w-12 shrink-0 h-full text-[8px] text-gray-400 dark:text-gray-500">
        {axisFractions.map((f, idx) => {
          const value = Math.round(maxV * f);
          if (idx === 0) {
            return <span key={idx} className="absolute top-0 right-0 whitespace-nowrap">{formatValue(value)}</span>;
          }
          if (idx === axisFractions.length - 1) {
            return <span key={idx} className="absolute bottom-0 right-0 whitespace-nowrap">{formatValue(value)}</span>;
          }
          return (
            <span key={idx} className="absolute right-0 -translate-y-1/2 whitespace-nowrap" style={{ top: `${(1 - f) * 100}%` }}>
              {formatValue(value)}
            </span>
          );
        })}
      </div>
      <div className="flex-1 flex items-end gap-2">
        {months.map((m, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
            <div className="flex items-end gap-[3px] w-full justify-center h-full">
              <div
                className="w-2.5 rounded-t"
                style={{ height: `${Math.max(3, Math.round((m[aKey] / maxV) * 100))}%`, backgroundColor: aColor }}
                title={`BMSL ${m.label}: ${formatValue(m[aKey])}`}
              />
              <div
                className="w-2.5 rounded-t"
                style={{ height: `${Math.max(3, Math.round((m[bKey] / maxV) * 100))}%`, backgroundColor: bColor }}
                title={`GP ${m.label}: ${formatValue(m[bKey])}`}
              />
            </div>
            <span className="text-[9px] text-gray-400 dark:text-gray-500 mt-1">{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vodorovný pruh (online role / fronta podpory / šarže databáze) ──────────
function BarRow({ label, value, max, color, sub, onClick }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      className={`mb-2 last:mb-0 ${clickable ? 'cursor-pointer group' : ''}`}
    >
      <div className="flex justify-between items-baseline mb-0.5">
        <span className={`text-xs ${clickable ? 'group-hover:underline' : ''} text-gray-700 dark:text-gray-300`}>{label}</span>
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{value}{sub ? ` ${sub}` : ''}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

// ── Vodorovný pruh se skladbou (podpora: tvorba smluv / technické / ostatní) ─
function StackedBarRow({ label, segments, total, sub }) {
  const max = Math.max(total, 1);
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex justify-between items-baseline mb-0.5">
        <span className="text-xs text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{total}{sub ? ` ${sub}` : ''}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
        {segments.filter((s) => s.value > 0).map((s, i) => (
          <div
            key={i}
            className="h-full transition-all"
            style={{ width: `${Math.round((s.value / max) * 100)}%`, backgroundColor: s.color }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
      </div>
    </div>
  );
}

// ── Modal se seznamem uživatelů dané role ────────────────────────────────────
function UsersModal({ title, users, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-sm max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-800 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto p-2">
          {users.length === 0 && <div className="text-sm text-gray-400 py-4 text-center">Žádní uživatelé.</div>}
          {users.map((u) => (
            <div key={u._id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <span className="text-sm text-gray-800 dark:text-gray-200">{u.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.online ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                {u.online ? '🟢 Online' : '⚪ Offline'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const ROLE_META = {
  obchodnik: { label: 'Obchodníci', color: '#3b82f6' },
  callcentrum: { label: 'Callcentrum', color: '#8b5cf6' },
  podpora: { label: 'Podpora', color: '#f59e0b' }
};

const REFRESH_MS = 60 * 1000;

export default function LiveStatsReport({ currentUserId, isDarkMode }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modalRole, setModalRole] = useState(null);
  const [dbOverride, setDbOverride] = useState(null); // dočasná volba uživatele databáze, dokud se neuloží
  const [savingDbUser, setSavingDbUser] = useState(false);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const dbParam = dbOverride ? `&databaseUserId=${dbOverride}` : '';
      const res = await fetch(`${API_URL}/admin/live-stats?userId=${currentUserId || ''}${dbParam}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setError(null);
        setLastUpdated(new Date());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Nepodařilo se načíst Live Stats.');
      }
    } catch {
      setError('Chyba připojení k serveru.');
    } finally {
      setLoading(false);
    }
  }, [currentUserId, dbOverride]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    intervalRef.current = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchData]);

  const handleSaveDbUser = async () => {
    const targetId = dbOverride || data?.databasePool?.selectedUserId;
    if (!targetId) return;
    setSavingDbUser(true);
    try {
      await fetch(`${API_URL}/admin/live-stats/database-user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ databaseUserId: targetId, actorUserId: currentUserId })
      });
      setDbOverride(null);
      await fetchData(true);
    } catch {
      alert('Nepodařilo se uložit výchozího uživatele databáze.');
    } finally {
      setSavingDbUser(false);
    }
  };

  if (loading && !data) {
    return <div className="text-center py-16 text-gray-400 dark:text-gray-500">Načítám Live Stats…</div>;
  }
  if (error && !data) {
    return <div className="text-center py-16 text-red-500">{error}</div>;
  }
  if (!data) return null;

  const { onlineByRole, obchodnik, callcentrum, bmslGp, support, databasePool, crmUtilization } = data;
  const currentHour = new Date().getHours();
  const dbSelectedId = dbOverride || databasePool.selectedUserId || '';
  const dbDirty = !!dbOverride && dbOverride !== databasePool.selectedUserId;

  return (
    <div className="h-full flex flex-col">
      {/* Ovládací lišta */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 shrink-0">
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {lastUpdated ? `Aktualizováno ${lastUpdated.toLocaleTimeString('cs-CZ')}` : ''}
          {error && <span className="text-red-500 ml-2">{error}</span>}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-obnovení (60s)
          </label>
          <button
            onClick={() => fetchData()}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            🔄 Obnovit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-3 flex-1 min-h-0">
        {/* 1. Online nyní + Podpora – fronta + Vytíženost CRM */}
        <Card title="🟢 Online nyní & 🛠 Podpora" isDarkMode={isDarkMode}>
          <div className="shrink-0 pb-2 mb-2 border-b border-gray-100 dark:border-gray-700">
            {Object.keys(ROLE_META).map((role) => {
              const r = onlineByRole[role] || { total: 0, online: 0, users: [] };
              return (
                <BarRow
                  key={role}
                  label={ROLE_META[role].label}
                  value={r.online}
                  max={r.total}
                  sub={`/ ${r.total}`}
                  color={ROLE_META[role].color}
                  onClick={() => setModalRole(role)}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[9px] text-gray-400 dark:text-gray-500 mb-1 shrink-0">
            <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm inline-block bg-blue-500" />Tvorba smluv</span>
            <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm inline-block bg-cyan-500" />Technické</span>
            <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm inline-block bg-gray-400" />Ostatní</span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 pb-2 mb-2 border-b border-gray-100 dark:border-gray-700">
            {support.byUser.length === 0 && <div className="text-xs text-gray-400 py-4 text-center">Žádní uživatelé podpory.</div>}
            {support.byUser.map((u) => (
              <StackedBarRow
                key={u.userId || 'unassigned'}
                label={u.userName}
                total={u.queue}
                sub={`ve frontě · ${u.processedThisMonth} zprac. tento měsíc`}
                segments={[
                  { label: 'Tvorba smluv', value: u.queueContract, color: '#3b82f6' },
                  { label: 'Technické', value: u.queueTechnical, color: '#06b6d4' },
                  { label: 'Ostatní', value: u.queueOther, color: '#9ca3af' }
                ]}
              />
            ))}
          </div>
          <div className="shrink-0">
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-[10px] text-gray-400">🔥 Vytíženost CRM – posledních 24 h</span>
              <span className="text-[10px] text-gray-400">{crmUtilization.reduce((s, h) => s + h.count, 0)} aktivit</span>
            </div>
            <div className="h-14">
              <MiniBarChart
                items={crmUtilization.map((h) => ({ label: `${fmt(h.hour)}:00`, value: h.count }))}
                color="#ef4444"
                highlightIndex={currentHour}
              />
            </div>
            <div className="flex justify-between text-[9px] text-gray-400 dark:text-gray-500 mt-1">
              <span>00:00</span>
              <span>12:00</span>
              <span>23:00</span>
            </div>
          </div>
        </Card>

        {/* 2. Obchodníci – aktivity + hodiny (30 dní) */}
        <Card title="📈 Obchodníci (30 dní)" isDarkMode={isDarkMode}>
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="text-[10px] text-gray-400 mb-0.5 shrink-0">Aktivity / den</div>
            <Sparkline points={obchodnik.activities30d.map((d) => d.count)} dates={obchodnik.activities30d.map((d) => d.date)} color="#3b82f6" />
          </div>
          <div className="flex-1 min-h-0 flex flex-col mt-1.5">
            <div className="text-[10px] text-gray-400 mb-0.5 shrink-0">Odpracováno / den</div>
            <Sparkline points={obchodnik.workMinutes30d.map((d) => d.minutes)} dates={obchodnik.workMinutes30d.map((d) => d.date)} color="#10b981" formatValue={formatDuration} />
          </div>
        </Card>

        {/* 3. Callcentrum – aktivity + hodiny (30 dní) */}
        <Card title="📞 Callcentrum (30 dní)" isDarkMode={isDarkMode}>
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="text-[10px] text-gray-400 mb-0.5 shrink-0">Aktivity / den</div>
            <Sparkline points={callcentrum.activities30d.map((d) => d.count)} dates={callcentrum.activities30d.map((d) => d.date)} color="#8b5cf6" />
          </div>
          <div className="flex-1 min-h-0 flex flex-col mt-1.5">
            <div className="text-[10px] text-gray-400 mb-0.5 shrink-0">Odpracováno / den</div>
            <Sparkline points={callcentrum.workMinutes30d.map((d) => d.minutes)} dates={callcentrum.workMinutes30d.map((d) => d.date)} color="#10b981" formatValue={formatDuration} />
          </div>
        </Card>

        {/* 4. Databáze – zbývající kontakty podle šarže */}
        <Card
          title="🗂 Databáze – zbývá kontaktů"
          isDarkMode={isDarkMode}
          right={<span className="text-sm font-bold text-gray-700 dark:text-gray-200">{databasePool.total}</span>}
        >
          <div className="flex gap-1 mb-2 shrink-0">
            <select
              value={dbSelectedId}
              onChange={(e) => setDbOverride(e.target.value)}
              className={`flex-1 text-xs px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">— vyberte uživatele —</option>
              {(databasePool.availableUsers || []).map((u) => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
            {dbDirty && (
              <button
                onClick={handleSaveDbUser}
                disabled={savingDbUser}
                title="Uložit jako výchozí"
                className="text-xs px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50"
              >
                💾
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            {(databasePool.batches || []).length === 0 && (
              <div className="text-xs text-gray-400 text-center py-2">Žádné kontakty.</div>
            )}
            {(databasePool.batches || []).map((b) => (
              <BarRow
                key={b.batchName}
                label={b.batchName}
                value={b.count}
                max={Math.max(...databasePool.batches.map((x) => x.count), 1)}
                color="#0891b2"
              />
            ))}
          </div>
        </Card>

        {/* 5. BMSL & GP (6 měsíců) + cíl */}
        <Card
          title="🎯 BMSL & GP (6 měsíců)"
          isDarkMode={isDarkMode}
          right={<span className="flex gap-2 text-[9px]"><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm inline-block" style={{ background: '#0ea5e9' }} />BMSL</span><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm inline-block" style={{ background: '#a855f7' }} />GP</span></span>}
        >
          <DualBarChart months={bmslGp.months} aKey="bmsl" bKey="gp" aColor="#0ea5e9" bColor="#a855f7" formatValue={kc} />
          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 text-center shrink-0">
            Cíl: <span className="font-semibold text-gray-600 dark:text-gray-300">{bmslGp.beforeSignatureTarget}</span> firem před podpisem
          </div>
        </Card>

        {/* 6. Callcentrum – vygenerované leady (30 dní) */}
        <Card title="🧲 Leady callcentra (30 dní)" isDarkMode={isDarkMode}>
          <Sparkline points={callcentrum.leadsGenerated30d.map((d) => d.count)} dates={callcentrum.leadsGenerated30d.map((d) => d.date)} color="#ec4899" />
          <div className="text-[10px] text-gray-400 text-center mt-1 shrink-0">
            Celkem za 30 dní: <span className="font-semibold text-gray-600 dark:text-gray-300">{callcentrum.leadsGenerated30d.reduce((s, d) => s + d.count, 0)}</span>
          </div>
        </Card>
      </div>

      {modalRole && (
        <UsersModal
          title={`${ROLE_META[modalRole].label} — ${(onlineByRole[modalRole] || {}).online || 0} / ${(onlineByRole[modalRole] || {}).total || 0} online`}
          users={(onlineByRole[modalRole] || {}).users || []}
          onClose={() => setModalRole(null)}
        />
      )}
    </div>
  );
}
